import { toggleAsset } from '@/model/ltcFinancialPlanningModel';
import { FormErrorAlert } from '@client/components/FormErrorAlert';
import { Button } from '@client/components/ui/button';
import { useAccountDetailForm } from '@client/hooks/useAccountDetailForm';
import { cn } from '@client/lib';
import { AppState } from '@client/model/store';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AccountDetailFields } from './AccountDetailFields';
import { ASSET_OPTIONS } from './AssetSelectionGrid';

interface AddAssetSectionProps {
  className?: string;
  skipDetails?: boolean;
}

interface AssetDetailFormProps {
  assetType: LtcFinancialPlanningAssetType;
  onSuccess: () => void;
}

function AssetDetailForm({ assetType, onSuccess }: AssetDetailFormProps) {
  const dispatch = useDispatch();

  const {
    formState,
    formErrors,
    formHasBeenSubmitted,
    hasRequiredFieldErrors,
    isValid: isFormValid,
    handleChangeField,
    handleSubmit: submitForm,
  } = useAccountDetailForm(assetType);

  const handleSubmit = () => {
    const success = submitForm();
    if (success) {
      dispatch(toggleAsset(assetType));
      onSuccess();
    }
  };

  return (
    <>
      <div className="bg-primary/5 mb-4 rounded-lg px-4 py-2">
        <p className="text-primary text-sm font-medium">
          Enter details for {ASSET_OPTIONS.find(a => a.type === assetType)?.label}
        </p>
      </div>

      <AccountDetailFields
        formState={formState}
        formErrors={formErrors}
        onChangeField={handleChangeField}
      />

      {/* Error Summary */}
      {formHasBeenSubmitted && !isFormValid && (
        <FormErrorAlert
          className="mt-6"
          mainErrorMessage="There is an error with your submission"
        >
          {hasRequiredFieldErrors && (
            <li>
              <span className="font-bold">Required field:</span> Please fill out all required
              fields.
            </li>
          )}
        </FormErrorAlert>
      )}

      {/* Submit Button */}
      <Button
        onClick={handleSubmit}
        className="mt-6 w-full"
      >
        Add Asset
      </Button>
    </>
  );
}

export function AddAssetSection({ className, skipDetails = false }: AddAssetSectionProps) {
  const dispatch = useDispatch();
  const { selectedAssets } = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning,
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState<LtcFinancialPlanningAssetType | null>(
    null,
  );

  const availableAssets = ASSET_OPTIONS.filter(asset => !selectedAssets.includes(asset.type));

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleAssetTypeSelect = (assetType: LtcFinancialPlanningAssetType) => {
    setSelectedAssetType(assetType);
  };

  const resetAll = () => {
    setSelectedAssetType(null);
    setIsExpanded(false);
  };

  const handleSkipDetailsSubmit = () => {
    if (!selectedAssetType) return;
    dispatch(toggleAsset(selectedAssetType));
    resetAll();
  };

  // Don't render if all assets are already selected
  if (availableAssets.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-dashed border-gray-300 bg-gray-50/50 text-left',
        className,
      )}
    >
      {/* Collapsed Header */}
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-100/50"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
            <Plus className="h-5 w-5" />
          </div>
          <div>
            <span className="font-semibold text-gray-900">Add another funding source</span>
            <p className="text-sm text-gray-500">
              Need to include an asset you didn't add earlier?
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
            {availableAssets.length} available
          </span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-white px-5 py-4">
          {/* Asset Type Selection */}
          <div className="mb-6">
            <label className="mb-2 block text-left text-sm font-medium text-gray-700">
              Select Asset Type
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {availableAssets.map(asset => (
                <button
                  key={asset.type}
                  type="button"
                  onClick={() => handleAssetTypeSelect(asset.type)}
                  className={cn(
                    'rounded-lg border-2 px-3 py-2.5 text-left text-sm font-medium transition-all',
                    {
                      'border-primary bg-primary/5 text-gray-900': selectedAssetType === asset.type,
                      'border-gray-200 bg-white text-gray-700 hover:border-gray-300':
                        selectedAssetType !== asset.type,
                    },
                  )}
                >
                  {asset.label}
                </button>
              ))}
            </div>
          </div>

          {/* When skipDetails is true, show save button immediately after selection */}
          {selectedAssetType && skipDetails && (
            <Button
              onClick={handleSkipDetailsSubmit}
              disabled={!selectedAssetType}
              className="w-full"
            >
              Add {ASSET_OPTIONS.find(a => a.type === selectedAssetType)?.label}
            </Button>
          )}

          {/* Account Details Form - Only rendered when asset type is selected and skipDetails is false */}
          {selectedAssetType && !skipDetails && (
            <AssetDetailForm
              assetType={selectedAssetType}
              onSuccess={resetAll}
            />
          )}

          {/* Help Text */}
          {!selectedAssetType && (
            <p className="text-center text-sm text-gray-500">
              Select an asset type above to add it as a funding source
            </p>
          )}
        </div>
      )}
    </div>
  );
}
